import type {
  FetchZoneDefinitionListParams,
  ZoneDefinition
} from 'Services/ProductManagement/Features/ZoneDefinition/types'
import type { ListResponse } from 'Services/ProductManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  ZONE_DEFINITION = 'ZoneDefinition'
}

export const zoneDefinitionApi = createBaseApi({
  reducerPath: 'zoneDefinitionApi',
  tagTypes: [TagTypes.ZONE_DEFINITION]
}).injectEndpoints({
  endpoints: builder => ({
    getZoneDefinitions: builder.query<
      ListResponse<ZoneDefinition>,
      // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
      FetchZoneDefinitionListParams | void
    >({
      query: params => ({
        url: '/api/v1/zone-definition/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ id }) => ({ type: TagTypes.ZONE_DEFINITION, id }) as const
              ),
              { type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }]
    }),
    getSingleZoneDefinition: builder.query<ZoneDefinition, string>({
      query: id => `/api/v1/zone-definition/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.ZONE_DEFINITION, id }]
    }),
    createSingleZoneDefinition: builder.mutation<ZoneDefinition, Partial<ZoneDefinition>>({
      query: data => ({
        url: `/api/v1/zone-definition/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }]
    }),
    updateSingleZoneDefinition: builder.mutation<ZoneDefinition, Partial<ZoneDefinition>>({
      query: data => ({
        url: `/api/v1/zone-definition/${data.id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: TagTypes.ZONE_DEFINITION, id }]
    }),
    deleteSingleZoneDefinition: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/zone-definition/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetZoneDefinitionsQuery,
  useGetSingleZoneDefinitionQuery,
  useCreateSingleZoneDefinitionMutation,
  useUpdateSingleZoneDefinitionMutation,
  useDeleteSingleZoneDefinitionMutation
} = zoneDefinitionApi
